import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import Cached from "@mui/icons-material/Cached";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { IconButton } from "@mui/material";
import moment from "moment";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { HeaderButton } from "components";
import Filters from "components/Filters";
import { CircularIndeterminate } from "components";
import { useService } from "components/route/ServiceRoute";
import { formatDateOnly, formatDateTime, GENERO_LABELS } from "consts/hotel";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { SFUsuarioHotelGet } from "services/services-mia/SFUsuarioHotelGet";
import { SFHabitacionGet } from "services/services-mia/SFHabitacionGet";
import { SFHotelUsuarioGet } from "services/services-mia/SFHotelUsuarioGet";
import { isReservaVisibleEnFecha } from "pages/hoteles/utils/ocupacionMatrix";
import {
    fetchWeekOccupancy,
    formatWeekRangeLabel,
    getWeekStart,
} from "pages/hoteles/utils/ocupacionMatrix";
import CellReservasModal from "./CellReservasModal";
import OcupacionMatrix from "./OcupacionMatrix";
import styles from "styles/pages/Pages.module.scss";
import matrixStyles from "./Ocupacion.module.scss";

export default function OcupacionHotel() {
    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);
    const { service } = useService();

    const [loading, setLoading] = useState(false);
    const [hoteles, setHoteles] = useState([]);
    const [habitaciones, setHabitaciones] = useState([]);
    const [matrix, setMatrix] = useState({});
    const [historico, setHistorico] = useState({});
    const [days, setDays] = useState([]);
    const [weekStart, setWeekStart] = useState(() => getWeekStart(moment()));
    const [cellModal, setCellModal] = useState({ isOpen: false });


    const loadHabitaciones = useCallback(
        async (hotelId) => {
            if (!hotelId) return [];
            const result = await SFHabitacionGet(credentials, {
                SFHotelId: Number(hotelId),
                SFHabitacionStatus: null,
            });
            if (!Array.isArray(result) || !result[0]?.SFHabitacionId) return [];
            return result
                .map((item) => ({
                    id: item.SFHabitacionId,
                    numero: item.SFHabitacionNumero,
                    piso: item.SFHabitacionPiso,
                    plazas: item.SFHabitacionPlazas,
                    estado: item.SFHabitacionStatus,
                    ultimoEstado: item.ultimoCambioStatusUFH,
                    historico: item.SFHabitacionHistoricoEstados,
                    genero:
                        GENERO_LABELS[item.SFHabitacionGenero] ??
                        item.SFHabitacionGenero,
                }))
                .sort((a, b) => {
                    const na = Number(a.numero);
                    const nb = Number(b.numero);
                    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
                    return String(a.numero).localeCompare(String(b.numero));
                });
        },
        [credentials]
    );

    const loadMatrix = useCallback(
        async (hotelId, start) => {
            if (!hotelId) {
                setMatrix({});
                setHistorico({});
                setDays([]);
                setHabitaciones([]);
                return {};
            }
            setLoading(true);
            setMatrix({});
            setHistorico({});
            try {
                const rooms = await loadHabitaciones(hotelId);
                setHabitaciones(rooms);
                const { matrix: weekMatrix, days: weekDays, historico: weekHistorico } = await fetchWeekOccupancy(
                    credentials,
                    SFHotelUsuarioGet,
                    hotelId,
                    start
                );
                setMatrix(weekMatrix);
                setHistorico(weekHistorico);
                setDays(weekDays);
                return weekMatrix;
            } finally {
                setLoading(false);
            }
        },
        [credentials, loadHabitaciones]
    );

    const formik = useFormik({
        initialValues: { hotelId: "" },
        onSubmit: () => loadMatrix(formik.values.hotelId, weekStart),
    });

    const loadHoteles = useCallback(async () => {
        const result = await SFUsuarioHotelGet(credentials);
        if (Array.isArray(result) && result[0]?.SFHotelId) {
            setHoteles(result);
            const firstHotelId = String(result[0].SFHotelId);
            if (!formik.values.hotelId) {
                formik.setFieldValue("hotelId", firstHotelId);
                const start = getWeekStart(moment());
                loadMatrix(firstHotelId, start);
            }
        }
    }, [credentials, formik, loadMatrix]);

    useEffect(() => {
        loadHoteles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (formik.values.hotelId) {
            loadMatrix(formik.values.hotelId, weekStart);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.hotelId, weekStart]);

    useEffect(() => {
        setRoute({
            wordKey: `${service?.servicioNombre || "Hoteles"} - Ocupación`,
        });
    }, [service?.servicioNombre, setRoute]);

    const goToPreviousWeek = () => {
        setWeekStart((prev) => moment(prev).subtract(1, "week"));
    };

    const goToNextWeek = () => {
        setWeekStart((prev) => moment(prev).add(1, "week"));
    };

    const goToCurrentWeek = () => {
        setWeekStart(getWeekStart(moment()));
    };

    const handleCellClick = ({ habitacion, dateKey, fechaLabel, guests }) => {
        setCellModal({
            isOpen: true,
            habitacion,
            dateKey,
            fechaLabel,
            reservas: guests.filter(g => isReservaVisibleEnFecha(g, dateKey)),
        });
    };

    const closeCellModal = () => {
        setCellModal({ isOpen: false });
    };

    const reloadCellAndMatrix = async () => {
        const weekMatrix = await loadMatrix(formik.values.hotelId, weekStart);
        setCellModal((prev) => {
            if (!prev.isOpen || !prev.habitacion || !prev.dateKey) return prev;
            const roomId = String(prev.habitacion.id);
            return {
                ...prev,
                reservas: weekMatrix[roomId]?.[prev.dateKey] ?? [],
            };
        });
    };

    const reload = () => loadMatrix(formik.values.hotelId, weekStart);

    const isCurrentWeek = moment(weekStart).isSame(getWeekStart(moment()), "day");

    return (
        <main className={styles.main}>
            <CellReservasModal
                open={cellModal.isOpen}
                onClose={closeCellModal}
                reservas={cellModal.reservas ?? []}
                habitacion={cellModal.habitacion}
                fechaLabel={cellModal.fechaLabel}
                hotelId={formik.values.hotelId}
                onRefresh={reloadCellAndMatrix}
            />

            <div className={styles["filters-actions"]}>
                <Filters
                    formik={formik}
                    hideSubmit
                    inputsRender={[
                        {
                            type: "select",
                            name: "hotelId",
                            label: "Hotel",
                            disabled: false,
                            options: hoteles,
                            firstOptionKey: "SFHotelId",
                            firstOptionName: "SFHotelNombre",
                            firstOptionValue: "SFHotelId",
                            all: false,
                            width: 260,
                        },
                    ]}
                    disabled={!formik.values.hotelId || loading}
                />
                <HeaderButton
                    text="Actualizar"
                    onClick={reload}
                    disabled={!formik.values.hotelId || loading}
                >
                    <Cached />
                </HeaderButton>
            </div>

            <div className={matrixStyles.weekNav}>
                <IconButton
                    aria-label="Semana anterior"
                    onClick={goToPreviousWeek}
                    disabled={!formik.values.hotelId || loading}
                >
                    <ChevronLeft />
                </IconButton>
                <span className={matrixStyles.weekLabel}>
                    {formatWeekRangeLabel(weekStart)}
                </span>
                <IconButton
                    aria-label="Semana siguiente"
                    onClick={goToNextWeek}
                    disabled={!formik.values.hotelId || loading}
                >
                    <ChevronRight />
                </IconButton>
            </div>

            <div className={styles.container}>
                {loading && !habitaciones.length ? (
                    <CircularIndeterminate />
                ) : (
                    <OcupacionMatrix
                        habitaciones={habitaciones}
                        days={days}
                        matrix={matrix}
                        historico={historico}
                        loading={loading}
                        onCellClick={handleCellClick}
                    />
                )}
            </div>
        </main>
    );
}
